import { ol_ImageLayer, ol_VectorLayer, ol_Feature, ol_Map, ol_source, ol_style, ol_geom, ol_layer } from "@omnia/exports/openlayers"
import { defineDirective } from "../../omnia"
import { ILayerCreateContext } from "../types"
import { getBestFitAngle } from "./best-fit-angle"
import { createGridCells } from "./cells"
import { OrientationInteraction } from "./interaction"

export class CreateGridCtrl {

    static create(): ng.IDirective {

        return defineDirective(CreateGridCtrl, "app/datalayers/edit/grid/grid.html", {
            context: "=",
        });

    }

    context: ILayerCreateContext

    form: ng.IFormController

    cellWidth: number = 50
    cellHeight: number = 50
    orientation: number = 0
    private offset: [number, number]

    private olmap: ol_Map
    private gridLayer: ol_VectorLayer
    private gridFeature: ol_Feature

    private cells: ol_geom.Geometry[]

    private orientInteraction: OrientationInteraction

    static $inject = ["$scope", "OmniaMapTopologyService", "OmniaMapMeasuringService"]

    constructor(private $scope: ng.IScope, private topo: omnia.map.IMapTopologyService, private measure: omnia.map.IMapMeasuringService) {
    }

    $onInit() {
        this.$scope.$watchGroup(["vm.context"], () => this.setup())
    }

    $onDestroy() {
        this.dispose()
    }

    get editContext() {
        return this.context && this.context.editContext
    }

    get targetLayer() {
        return this.context && this.context.layer
    }

    get layerType() {
        return this.editContext && this.editContext.layerType
    }

    private setup() {

        this.dispose()
        if (!this.context) return

        const map = this.editContext.map

        this.olmap = map.getMap()

        this.gridLayer = new ol_layer.Vector({
            source: new ol_source.Vector({ features: [] }),
            style: new ol_style.Style({
                fill: new ol_style.Fill({ color: "#ffffff30" }),
                stroke: new ol_style.Stroke({ color: "#000" })
            })
        })

        this.gridFeature = null

        this.olmap.addLayer(this.gridLayer)

        this.orientInteraction = new OrientationInteraction(this.olmap)

        const geometry = this.editContext.outerGeometry
        const centroid = this.topo.getCentroid(geometry)

        this.orientInteraction.setup(geometry, centroid, {
            getGridFeature: () => this.gridFeature,
            getOrientation: () => this.orientation,
            setOrientation: value => this.setOrientation(value, false),
            setOffset: offset => this.setOffset(offset)
        })

        this.refreshGrid()

    }

    private dispose() {

        if (this.orientInteraction) {
            this.orientInteraction.dispose()
            this.orientInteraction = null
        }

        if (this.olmap && this.gridLayer) {
            this.olmap.removeLayer(this.gridLayer)
            this.gridLayer = null
            this.olmap = null
        }

        this.cells = null

    }

    canCreate() {
        return this.targetLayer && this.cells && (this.cells.length > 0)
    }

    create() {

        if (!this.canCreate()) return

        this.targetLayer.removeAllZones()
        this.cells.forEach(c => this.targetLayer.createNewZoneWithGeometry(c))

        this.context.create()

    }

    setBestOrientation() {
        if (!this.editContext) return
        const b = getBestFitAngle(this.editContext.outerGeometry, this.topo)
        const o = Math.round(b * 180 / Math.PI) % 180
        this.setOrientation(o)
    }

    setOrientation(orientation: number, resetInteraction: boolean = true) {

        if (!this.editContext) return

        this.orientation = Math.round(orientation)
        this.$scope.$applyAsync()

        this.refreshGrid(resetInteraction)

    }

    private setOffset(offset: [number, number]) {
        this.offset = offset
        this.refreshGrid(false)
    }

    private refreshGrid(resetInteraction: boolean = true) {

        this.cells = null

        if (!this.gridLayer) return
        if (!this.editContext) return

        const geometry = this.editContext.outerGeometry
        if (!geometry) return

        const cellSpec = {
            width: Math.max(this.cellWidth || 50, 8),
            height: Math.max(this.cellHeight || 50, 8),
            angle: this.orientation / (180 / Math.PI),
            offset: this.offset
        }

        const cells = createGridCells(geometry, cellSpec, this.topo)

        const collection = new ol_geom.GeometryCollection(cells)

        const feature = this.gridFeature || new ol_Feature()
        feature.setGeometry(collection)

        if (feature != this.gridFeature) {
            this.gridFeature = feature
            const source = this.gridLayer.getSource() as ol_source.Vector
            source.addFeature(feature)
        }

        this.cells = cells

        if (resetInteraction) {
            this.orientInteraction.refresh()
        }

    }

}
